// src/onboarding/activation/Day1QuestIntro.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import ChartPuzzle from "./ChartPuzzle";


const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const seriesBase = [
  { time: 1, open: 100, high: 102, low: 99, close: 101 },
  { time: 2, open: 101, high: 103, low: 100, close: 102 },
  { time: 3, open: 102, high: 103, low: 98, close: 99 },
  { time: 4, open: 99, high: 100, low: 96, close: 97 },
  { time: 5, open: 97, high: 100, low: 96, close: 99 },
  { time: 6, open: 99, high: 104, low: 98, close: 103 },
  { time: 7, open: 103, high: 106, low: 102, close: 105 },
  { time: 8, open: 105, high: 106, low: 103, close: 104 },
];

const dataBreakout = {
  candles: seriesBase,
  resistance: 104,
  answerIndex: 6,
};

const dataSupportBounce = {
  candles: [
    { time: 1, open: 100, high: 100, low: 96, close: 97 },
    { time: 2, open: 97, high: 98, low: 95, close: 96 },
    { time: 3, open: 96, high: 100, low: 95, close: 99 },
    { time: 4, open: 99, high: 101, low: 98, close: 100 },
    { time: 5, open: 100, high: 102, low: 99, close: 101 },
  ],
  support: 95,
  answerIndex: 2,
};

// ✅ removed the stray "const"
const dataEngulfing = {
  candles: [
    { time: 1, open: 103, high: 104, low: 100, close: 101 },
    { time: 2, open: 100, high: 106, low: 99, close: 105 },
    { time: 3, open: 105, high: 106, low: 103, close: 104 },
    { time: 4, open: 104, high: 105, low: 102, close: 103 },
  ],
  pairIndex: [0, 2],
};

export default function Day1QuestIntro() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("egg");
  const [tokens, setTokens] = useState(100);
  const [xp, setXp] = useState(0);
  const [growth, setGrowth] = useState(0);

  const reward = async (xpAdd = 10, tokAdd = 10, grow = 8) => {
    setXp((x) => x + xpAdd);
    setTokens((t) => t + tokAdd);
    setGrowth((g) => Math.min(100, g + grow));
    confetti({ particleCount: 45, spread: 50, origin: { y: 0.3 } });
    await wait(300);
  };

  const next = (p) => setPhase(p);

  return (
    <div className="quest">
      <div className="topbar">
        <div className="brand">🐂 FinTorro</div>
        <div className="meta">
          <span className="pill">🪙 {tokens}</span>
          <span className="pill">✨ {xp}</span>
        </div>
      </div>

      {phase === "egg" && (
        <section className="card hero">
          <div className="egg">🥚</div>
          <h1>Torro’s First Quest</h1>
          <p className="sub">Torro grows when you learn & practice.</p>
          <div className="growth"><div className="growth-bar" style={{ width: `${growth}%` }} /></div>
          <button className="btn-primary" onClick={() => next("p1")}>Begin</button>
        </section>
      )}

      {phase === "p1" && (
        <section className="card">
          <ChartPuzzle
            title="NEW MOVE"
            prompt="Drag the flag onto the candle that breaks above resistance."
            data={dataBreakout}
            mode="drag-flag"
            onCorrect={async () => { await reward(10, 10, 10); next("p2"); }}
            onWrong={() => alert("Hint: look for a close ABOVE the purple line")}
          />
        </section>
      )}

      {phase === "p2" && (
        <section className="card">
          <ChartPuzzle
            title="NEW MOVE"
            prompt="Tap the candle that bounces up from support."
            data={dataSupportBounce}
            mode="tap-candle"
            onCorrect={async () => { await reward(10, 10, 10); next("p3"); }}
            onWrong={() => alert("Try the first candle that turns up AFTER touching the blue line")}
          />
        </section>
      )}

      {phase === "p3" && (
        <section className="card">
          <ChartPuzzle
            title="NEW PATTERN"
            prompt="Drag across the two candles that form a bullish engulfing."
            data={dataEngulfing}
            mode="drag-tag-pair"
            onCorrect={async () => { await reward(20, 30, 20); next("complete"); }}
            onWrong={() => alert("Look for a big green candle that swallows the prior red body")}
          />
        </section>
      )}

      {phase === "complete" && (
        <section className="card done">
          <div className="torro">🐂</div>
          <h2>Quest Complete!</h2>
          <ul className="rewards">
            <li>✨ +40 XP</li>
            <li>🪙 +50 Tokens</li>
            <li>🏅 Badge: Chart Rookie</li>
          </ul>
          <button className="btn-primary" onClick={() => navigate("/onboarding/day1")}>
            Ask Torro your first trading question
          </button>
          <button className="btn-secondary" onClick={() => setPhase("p1")}>Replay Quest</button>
        </section>
      )}

      <style>{`
        .quest { background:#0B0D11; color:#DDE2EA; min-height:100vh; padding:16px; }
        .topbar { display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; }
        .brand { font-weight:800; }
        .meta { display:flex; gap:8px; }
        .pill { background:#12151B; border:1px solid #1E232B; padding:6px 10px; border-radius:999px; font-size:13px; }
        .card { max-width:960px; margin:20px auto; background:#0F141B; border:1px solid #1E2630; border-radius:16px; padding:16px; text-align:center; }
        .hero .egg { font-size:96px; margin:6px 0 8px; animation: bob 1.6s ease-in-out infinite; }
        @keyframes bob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        .sub { color:#9FB0C5; margin:6px 0 12px; }
        .growth { height:8px; background:#121821; border-radius:999px; margin:0 auto 12px; max-width:360px; overflow:hidden; }
        .growth-bar { height:100%; background:#FF7C24; }
        .btn-primary { background:#FF7C24; color:#000; border:none; border-radius:12px; padding:10px 14px; font-weight:800; cursor:pointer; }
        .btn-secondary { background:#131B24; color:#DDE2EA; border:1px solid #253041; border-radius:12px; padding:10px 14px; margin-left:8px; }
        .rewards { list-style:none; padding:0; margin:10px 0; display:flex; gap:16px; justify-content:center; flex-wrap:wrap; }
        .torro { font-size:56px; }
      `}</style>
    </div>
  );
}




